import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AqlBuilderFilter from '@Inventory/AqlBuilder/organisms/AqlBuilderFilter';
import {
  AqlFieldExpression,
  AqlBuilderField,
  AqlBuilderFieldName,
  AqlBuilderFieldOperators,
  AqlFilterItem,
  QUERY_QP,
  useGetQueryQP,
} from '@Inventory/AqlBuilder/helpers/common';
import { filtersToQuery, queryToFilters } from '@Inventory/AqlBuilder/helpers/aql-functions';
import ListFilters, { ListFiltersFilter } from '@common/ui/ListFilters/ListFilters';
import { PAGE_PARAM } from '@api';
import { SaveQueryButton } from '@Inventory/organisms/SaveQueryButton';

const useUrlSync = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  return useCallback(
    (filters: AqlFilterItem[]) => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete(QUERY_QP);
      const query = filtersToQuery(filters);
      if (query) {
        newSearchParams.set(QUERY_QP, query);
      }
      if (newSearchParams.has(PAGE_PARAM)) {
        newSearchParams.delete(PAGE_PARAM);
      }
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );
};

const useFiltersList = () => {
  const query = useGetQueryQP();

  const filtersList = useMemo(() => {
    const parsed = query ? queryToFilters(query) : [];
    return Object.values(AqlBuilderField).map((field) => parsed.find((filter) => filter.field === field) || { field });
  }, [query]);

  return { filtersList, activeFilters: filtersList.filter((f) => f.expression) };
};

const useVisibleFilters = (filtersList: AqlFilterItem[]) => {
  const [openedFilter, setOpenedFilter] = useState<string | undefined>(undefined);

  const visibleFilters = useMemo(() => {
    const list = new Set<AqlBuilderField>([
      AqlBuilderField.integration,
      AqlBuilderField.resource_type,
      AqlBuilderField.resource_status,
    ]);

    if (openedFilter) {
      list.add(openedFilter as AqlBuilderField);
    }

    filtersList.forEach((f) => {
      if (f.expression) {
        list.add(f.field);
      }
    });

    return Array.from(list);
  }, [filtersList, openedFilter]);

  return {
    visibleFilters,
    openedFilter,
    addVisibleFilter: (v?: string) => setOpenedFilter(v),
  };
};

export default function AqlBuilderFilters() {
  const syncUrlWithFilters = useUrlSync();
  const { filtersList, activeFilters } = useFiltersList();

  const { visibleFilters, addVisibleFilter, openedFilter } = useVisibleFilters(filtersList);

  const onFieldValueChange = useCallback(
    (field: string, value?: AqlFieldExpression) => {
      const newFilters = filtersList.map((filter) =>
        filter.field === field ? { ...filter, expression: value } : filter,
      );
      syncUrlWithFilters(newFilters);
    },
    [filtersList, syncUrlWithFilters],
  );

  const filterOptions: ListFiltersFilter[] = useMemo(
    () =>
      filtersList.map((f) => ({
        name: AqlBuilderFieldName[f.field],
        queryParam: f.field,
        element: (
          <AqlBuilderFilter
            key={f.field}
            field={f.field}
            operators={AqlBuilderFieldOperators[f.field]}
            expression={f.expression}
            onExpressionChange={(newValue) => onFieldValueChange(f.field, newValue)}
            label={AqlBuilderFieldName[f.field]}
            opened={openedFilter === f.field}
            setOpened={(opened) => addVisibleFilter(opened ? f.field : undefined)}
          />
        ),
      })),
    [addVisibleFilter, filtersList, onFieldValueChange, openedFilter],
  );

  const onHandleClear = useCallback(() => {
    syncUrlWithFilters([]);
  }, [syncUrlWithFilters]);

  return (
    <ListFilters
      visibleFilters={visibleFilters}
      filters={filterOptions}
      onAddFilter={(q) => addVisibleFilter(q as AqlBuilderField)}
      onClear={activeFilters.length > 0 ? onHandleClear : undefined}
      suffix={activeFilters.length > 0 ? <SaveQueryButton /> : undefined}
    />
  );
}
